/**
 * WS-C §6 — the composition root and the auth gate.
 *
 * FIVE BRANCHES, IN THIS ORDER, exactly one rendering. The order is the whole design:
 *
 *   0. `pathname === CONSENT_PATH` → `OAuthConsentPage`, chromeless, **above all four below** —
 *      `&& !mustChangePassword`     but ⛔ SUBORDINATE to branch 3. See `ConsentGate`: the consent
 *                                   card must render in three of the four auth states below, and
 *                                   the one it must NOT render in is the one in the middle.
 *   1. `auth.isLoading`          → the spinner. ⛔ FIRST of the four, and this is the entire reason
 *                                  `isLoading` exists. `useAuth` reports `isAuthenticated: false`
 *                                  on its first render and flips asynchronously; bouncing on
 *                                  branch 2 before the restore completes kicks EVERY returning
 *                                  operator to `/login` on EVERY page load.
 *   2. `!auth.isAuthenticated`   → the auth routes only. ⛔ The OAuth callback must be reachable
 *                                  HERE, unauthenticated — that is the whole point of a callback.
 *   3. `auth.mustChangePassword` → `ChangePasswordPage` for every path. ⛔ ABOVE branch 4 AND above
 *                                  branch 0, and both are security requirements (see that page's
 *                                  header, and `ConsentGate`'s).
 *   4. otherwise                 → the provider stack + `AuthenticatedShell`.
 *
 * ⚠️ Branch 0 is numbered from zero deliberately: it is not a fifth case appended to the ternary,
 * it WRAPS the ternary. A reader who edits the four-way chain never has to keep it in mind — which
 * is the property that made this shape the ruling (R-20, revised).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import {
  MotionProvider,
  PermissionProvider,
  type PermissionSnapshot,
} from '@zion/sdk-react';
import { hasPermission } from '@zion/sdk-contracts';
import { toSummaryState, type AvailabilitySummaryState } from '../panes/availability-model';
import { AVAILABILITY_SUMMARY_PERMISSION, fetchAvailabilitySummary } from '../api/appAvailabilitySummary.api';
import {
  AvailabilitySummaryProvider,
  visibleSummaryState,
} from './availability-summary-context';
import {
  AccessRequestSummaryProvider,
  visibleAccessRequestSummary,
  type AccessRequestSummaryValue,
} from './access-request-summary-context';
import {
  ACCESS_REQUEST_READ_PERMISSION,
  getAccessRequestSummary,
  type AccessRequestSummary,
} from '../api/accessRequests.api';
import { ApiError } from '../platform/http/client';
import { apiErrorMessage } from '../platform/errors';
import type { AsyncState } from '../panes/async-state';
import { useAuth } from '../platform/auth/useAuth';
import { getCapabilities, getMyGrants, type AuthCapabilities } from '../platform/auth/auth.api';
import { BASENAME } from '../platform/paths';
import { CpBrandingProvider } from '../branding/CpBrandingContext';
import {
  claimGrafanaReturn,
  grafanaReturnTargetOf,
  openGrafanaPath,
  stashReturnTo,
  takeReturnTo,
} from '../platform/auth/return-to';
import { REFUSAL_PATHS } from '../platform/auth/refusal-paths';
import { LoginPage, CAPABILITIES_FALLBACK } from '../pages/LoginPage';
import { OAuthCallbackPage } from '../pages/OAuthCallbackPage';
import { SignInUnavailablePage } from '../pages/SignInUnavailablePage';
import { SignInIncompletePage } from '../pages/SignInIncompletePage';
import { AccessRequestedPage } from '../pages/AccessRequestedPage';
import { OAuthConsentPage, consentAuthorityOf, consentElevateOf } from '../pages/OAuthConsentPage';
import { ChangePasswordPage } from '../pages/ChangePasswordPage';
import { SECOND_FACTOR_PATH, SecondFactorPage, takeSecondFactorFragment } from '../pages/SecondFactorPage';
import { STEP_UP_PATH_PREFIX, StepUpPage, stepUpIdFromPath } from '../pages/StepUpPage';
import { StepUpProvider } from '../platform/auth/StepUpDialog';
import { ActiveRoleProvider, useActiveRole } from './active-role-context';
import {
  approveAuthorizeRequest,
  denyAuthorizeRequest,
  getAuthorizeRequest,
} from '../api/oauthConsent.api';
import { AppsProvider, useAppsContext } from './apps-context';
import { DataPlaneChoicesProvider } from './data-plane-choices-context';
import { ProfileProvider } from './profile-context';
import { AuthCardFrame, AuthPendingState } from './AuthCardFrame';
import { Button } from './Button';
import { ButtonLink } from './ButtonLink';
import { AppRegistryProvider } from './AppRegistryContext';
import { AuthenticatedShell } from './AuthenticatedShell';
import { GrantsReloadProvider, GrantsStateProvider } from './grants-context';
import {
  buildPermissionSnapshot,
  grantsStateFromError,
  grantsStateFromResponse,
  settleFetchedGrants,
  visibleGrantsRereadError,
  visibleGrantsState,
  type FetchedGrants,
  type GrantsState,
} from './grants-state';

/**
 * ⛔ Re-exported so `src/__tests__/base-path.build.test.ts` can import it from the path WS-A §6
 * specifies, while the single definition stays in `src/platform/paths.ts` (the http client needs
 * it too, and `src/platform/` must not import from `src/shell/`).
 *
 * It must equal `vite.config.ts`'s `base` minus the trailing slash. That build test is the only
 * mechanical link.
 */
export { BASENAME };

/**
 * Record the destination, then send the visitor to sign in.
 *
 * ⛔ A component rather than an inline `<Navigate>`, because the stash must happen during RENDER of
 * the route that matched — an effect would run after the redirect had already been committed, and
 * `window.location` would by then report `/cp-admin/login`.
 */
function StashAndRedirectToLogin(): React.JSX.Element {
  stashReturnTo();
  return <Navigate to="/login" replace />;
}

/**
 * Branch 3's page: record the destination, then render the rotation form (P-03 of
 * `design-docs/WS-9.md` section 6, WS-9b-R3, WS-9b-R15).
 *
 * A forced rotation interrupts the operator as a sign-in does, so the destination must survive it.
 * Two paths reach branch 3 at a destination that no stash holds:
 *
 * 1. A signed-in tab with `mustChangePassword` opens a deep link, such as `/step-up/<id>` from the
 *    CLI. `StashAndRedirectToLogin` renders only in branch 2, so nothing else writes the stash.
 * 2. A sign-in through the second factor, or through the OAuth callback, already took the stash and
 *    navigated to the destination. `takeReturnTo` removes the stash on that read.
 *
 * The effect stashes the browser path at each location that branch 3 renders, so
 * `ChangePasswordPage` takes the destination after the rotation. `stashReturnTo` skips `/login` and
 * its sub-paths, so a render at `/login` or `SECOND_FACTOR_PATH` keeps the stash of the sign-in.
 *
 * An effect, because no redirect follows the stash here: `window.location` already reports the
 * matched path after the commit. The router's `pathname` and `search` are the dependencies, so a
 * navigation inside branch 3 records the new path. The rotation's own navigation leaves branch 3 in
 * the same render that clears `mustChangePassword`, so the stash that the page took stays empty.
 */
function StashAndChangePassword({
  onChangePassword,
}: {
  readonly onChangePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}): React.JSX.Element {
  const { pathname, search } = useLocation();
  useEffect(() => {
    stashReturnTo();
  }, [pathname, search]);
  return <ChangePasswordPage onChangePassword={onChangePassword} takeReturnTo={takeReturnTo} />;
}

/**
 * Where `oauth-as.service.ts:235` sends an authenticated operator. ⛔ Router-relative.
 *
 * ⛔⛔ **EXPORTED, AND THE LITERAL APPEARS NOWHERE ELSE IN THE PACKAGE** — not in
 * `OAuthConsentPage.tsx`, not in `oauthConsent.api.ts`, not in a test's `setBrowserPath` call. Every
 * consumer imports it, and `routing-branches.test.tsx` scans the whole non-test corpus to prove
 * there is exactly one occurrence and that it is this declaration.
 *
 * ⚠️ Same rule `platform/paths.ts` states for `BASENAME` (*"if you find yourself writing the literal
 * `'/cp-admin'` anywhere else, that is the bug"*), and for the same reason: this string is a
 * **contract with a producer in another service**, so a second copy is a second place for the two to
 * drift apart with nothing to notice.
 */
export const CONSENT_PATH = '/oauth/consent';

/**
 * The OAuth redirect is a TOP-LEVEL navigation to a third-party origin, so it cannot go through the
 * router. Hoisted to module scope for reference stability, and kept as the page's one seam so the
 * page itself never touches `window.location`.
 */
function navigateExternalDefault(url: string): void {
  window.location.href = url;
}

/**
 * ⛔ **MOUNTED BY LOCATION BRANCH, NOT BY `<Route>`, AND THAT IS A RULING RATHER THAN A STYLE**
 * (spine R-20, revised).
 *
 * The chromeless card must render in THREE of the four auth states, and the state it must NOT render
 * in is the one in the middle. A `<Route>` inside branch 2 reaches the unauthenticated arm only; a
 * `<Route>` inside `AppHost` puts the card inside `SectionLayout` chrome; and hoisting
 * `AuthenticatedShell` under a parent `<Route path="*">` is the shape `AppHost.tsx` and
 * `SiteAdminApp.tsx` both record BREAKING descendant routing — the parent splat consumed the matched
 * prefix and every workspace route stopped matching. ⇒ one branch, above all four.
 *
 * ⛔ **A two-mechanism shape was considered and RULED AGAINST — do not reintroduce it.** (A
 * `<Route path="/oauth/consent">` inside branch 2 *plus* a gate scoped to branch 4.) Two mechanisms
 * for one screen means a reader must hold the asymmetry in their head and a later edit to either arm
 * silently changes the screen in only one auth state; it also makes the `isLoading` case render the
 * console splash on a page that is not the console.
 *
 * ⛔⛔ **`mustChangePassword` OUTRANKS THIS GATE, AND THAT IS A SECURITY REQUIREMENT, NOT A UX
 * PREFERENCE.** An operator whose password we are *forcing* them to rotate is by definition one
 * whose current credential we no longer trust. Approving an OAuth grant mints a long-lived delegated
 * token that **survives the password change** — so letting consent render first makes the compelled
 * rotation cosmetic: whoever holds the old password walks away with a credential the new password
 * does not revoke. ⚠️ In this console the property is doubly held, because branch 3 already renders
 * the rotation form for EVERY path — so removing this clause would not by itself expose consent. The
 * clause stays anyway, and is asserted with its own inversion control, because **a defence that
 * depends on another branch's ordering is a defence one refactor from being gone.**
 *
 * ⚠️ `useLocation().pathname` is BASENAME-STRIPPED, so this comparison is correct both before and
 * after the R-2 cutover flips `BASENAME` from `/cp-admin` to `''`.
 *
 * The page sits inside its own `StepUpProvider` and `ActiveRoleProvider` (plan
 * 2026-10-28-identity-mcp-tools, R-020 item 4), with the same session values that branch 4 hands
 * them. The consent page then offers the passkey elevation to `PLATFORM_ADMIN_ROLE` when the view
 * names a scope that needs it. Branch 4 is not mounted while this gate answers, so one provider
 * pair at most is live. Signed out or loading, the token carries no `heldSiteRoles` claim, the
 * provider gives no value, and the page offers no elevation.
 */
function ConsentGate({
  auth,
  children,
}: {
  readonly auth: ReturnType<typeof useAuth>;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const { pathname } = useLocation();
  if (pathname === CONSENT_PATH && !auth.mustChangePassword) {
    return (
      <StepUpProvider>
        <ActiveRoleProvider
          operatorId={auth.operatorId}
          heldSiteRoles={auth.heldSiteRoles}
          siteRoles={auth.siteRoles}
          restSiteRole={auth.restSiteRole}
          elevatedUntilMs={auth.elevatedUntilMs}
          switchActiveRole={auth.switchActiveRole}
          refreshSession={auth.refreshSession}
        >
          <ConsentPageMount auth={auth} />
        </ActiveRoleProvider>
      </StepUpProvider>
    );
  }
  return <>{children}</>;
}

/**
 * The consent page with its production collaborators. It reads the active role of the provider
 * above it, so the elevation the page offers follows the session (`consentElevateOf`), and the
 * page reads its view again when the session authority changes (`consentAuthorityOf`). The page
 * times the elevation end on the clock of the provider.
 */
function ConsentPageMount({ auth }: { readonly auth: ReturnType<typeof useAuth> }): React.JSX.Element {
  const activeRole = useActiveRole();
  const elevate = consentElevateOf(activeRole);
  return (
    <OAuthConsentPage
      isAuthenticated={auth.isAuthenticated}
      isLoading={auth.isLoading}
      loadRequest={getAuthorizeRequest}
      approve={approveAuthorizeRequest}
      deny={denyAuthorizeRequest}
      navigateExternal={navigateExternalDefault}
      elevate={elevate}
      authority={consentAuthorityOf(auth.operatorId, activeRole)}
      now={activeRole?.now}
    />
  );
}

/**
 * The second-factor gate (kernel apps, WS-9b, `design-docs/WS-9.md` section 3.6). It wraps every
 * other branch, `ConsentGate` included.
 *
 * At `SECOND_FACTOR_PATH` it takes the `#sf=` fragment once, with a ref guard for StrictMode, through
 * the router's `navigate`, so the router state drops the fragment too (F-02). A token goes to
 * `auth.resumeSecondFactor`. Until that effect ran it shows the pending state, so the
 * page never flashes the invalid-link card before the token arrives.
 *
 * While `auth.secondFactor` is set it renders `SecondFactorPage` for every path and in every auth
 * state. So the OAuth arm of a CLI login works in a tab with a console session, and that session
 * stays as it was (F-10). At `SECOND_FACTOR_PATH` with no wait it renders the page with
 * `challenge: null`. Else it renders its children.
 */
function SecondFactorGate({
  auth,
  children,
}: {
  readonly auth: ReturnType<typeof useAuth>;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const atPath = location.pathname === SECOND_FACTOR_PATH;
  const takenRef = useRef(false);
  const [taken, setTaken] = useState(false);
  const { resumeSecondFactor } = auth;

  /*
   * ⛔ The router's location and the router's `navigate`, never `window.location` and a raw
   * `history.replaceState` (WS-9b-R13). A raw call fires no `popstate`, so `BrowserRouter` keeps the
   * `#sf=` token in its React state for the life of the page (F-02). And a passive effect, never a
   * layout effect: `BrowserRouter` subscribes to its history in its own layout effect, which runs
   * after the layout effects of its children, so a replace from a child layout effect at mount
   * reaches no listener.
   */
  useEffect(() => {
    if (!atPath || takenRef.current) return;
    takenRef.current = true;
    const token = takeSecondFactorFragment(location, (path) => {
      void navigate(path, { replace: true });
    });
    if (token !== null) resumeSecondFactor(token);
    setTaken(true);
  }, [atPath, location, navigate, resumeSecondFactor]);

  if (auth.secondFactor !== null || atPath) {
    if (auth.secondFactor === null && !taken) {
      return <AuthPendingState label="Reading the sign-in link…" testId="second-factor-taking" />;
    }
    return (
      <SecondFactorPage
        challenge={auth.secondFactor}
        onReadCeremony={auth.readSecondFactorCeremony}
        onAbandon={auth.abandonSecondFactor}
        onComplete={auth.completeSecondFactor}
        onProveRecovery={auth.proveRecovery}
        onSendRecoveryEmail={auth.sendRecoveryEmail}
        onExchangeCode={auth.loginWithOAuthCode}
        takeReturnTo={takeReturnTo}
      />
    );
  }
  return <>{children}</>;
}

/**
 * The step-up page gate (kernel apps, WS-9b). Under `STEP_UP_PATH_PREFIX`, for a signed-in operator
 * who owes no password change, it renders `StepUpPage` chromeless. Else it renders its children, so
 * the link survives the interruption (P-03). A signed-out visit reaches `StashAndRedirectToLogin`,
 * and a visit that owes a rotation reaches `StashAndChangePassword` in branch 3.
 */
function StepUpGate({
  auth,
  children,
}: {
  readonly auth: ReturnType<typeof useAuth>;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const { pathname } = useLocation();
  if (pathname.startsWith(STEP_UP_PATH_PREFIX) && auth.isAuthenticated && !auth.mustChangePassword) {
    return <StepUpPage requestId={stepUpIdFromPath(pathname)} />;
  }
  return <>{children}</>;
}

/**
 * The card a session that existed before the redirect sees in place of an automatic load (review
 * finding security-1). Only a click on its `Button`, or Enter or Space on it, loads `target`. A link
 * from another site can open the console URL that names a Grafana path, and the console session
 * then restores, but the `SameSite=Strict` cookie goes to Grafana only after that deliberate act.
 */
function GrafanaContinueCard({ target }: { readonly target: string }): React.JSX.Element {
  return (
    <AuthCardFrame
      title="Open Grafana"
      subtitle="You are signed in. Grafana opens when you continue."
      /* The card replaces the page after a redirect the person did not start, so focus goes to
         the title, never to the action. `SignInIncompletePage` follows the same rule. */
      autoFocusTitle
      footer={<span className="break-all">{target}</span>}
      testId="grafana-continue"
    >
      <Button tone="primary" size="md" onClick={() => openGrafanaPath(target)} data-testid="grafana-continue-open">
        Continue to Grafana
      </Button>
      <ButtonLink to="/" size="md" data-testid="grafana-continue-stay">
        Stay in the console
      </ButtonLink>
    </AuthCardFrame>
  );
}

/** What the gate's effect decided for one target. */
interface GrafanaReturnOutcome {
  readonly target: string;
  readonly kind: 'refused' | 'confirm';
}

/**
 * The Grafana return gate (plan 2026-10-07-grafana-host-and-login-redirect, decision 5). It wraps
 * branch 4, so it acts only for a signed-in operator who owes no password change.
 *
 * Grafana sits behind the console's origin at `/grafana`, and the router cannot reach it: a router
 * location under `/grafana` would fall to `AppHost`'s catch-all. So when `grafanaReturnTargetOf`
 * names a target, the gate's effect takes one return with `claimGrafanaReturn`:
 *
 * 1. The loop guard refuses it: the gate renders its children, and `AppHost` sends the operator to
 *    the landing page.
 * 2. The operator signed in in this document (`signedInHere`): the gate calls
 *    `window.location.assign` with the relative, same-origin target at once, a full page load that
 *    carries the session cookie to Grafana. The pending state shows until the browser leaves.
 * 3. The session existed before this document: the gate shows `GrafanaContinueCard`, and the page
 *    load waits for the operator's click.
 *
 * Two routes reach a target. A sign-in page that consumed a Grafana stash navigates the router to
 * `/grafana/...`. A session at `/login?next=/grafana/...` renders branch 4 there.
 *
 * A ref holds the target the effect took, so StrictMode's second effect run takes nothing.
 */
function GrafanaReturnGate({
  signedInHere,
  children,
}: {
  readonly signedInHere: boolean;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const location = useLocation();
  const target = grafanaReturnTargetOf(location);
  const takenRef = useRef<string | null>(null);
  const [outcome, setOutcome] = useState<GrafanaReturnOutcome | null>(null);

  useEffect(() => {
    if (target === null || takenRef.current === target) return;
    takenRef.current = target;
    if (!claimGrafanaReturn()) setOutcome({ target, kind: 'refused' });
    else if (signedInHere) openGrafanaPath(target);
    else setOutcome({ target, kind: 'confirm' });
  }, [target, signedInHere]);

  if (target === null) return <>{children}</>;
  if (outcome?.target === target) {
    return outcome.kind === 'refused' ? <>{children}</> : <GrafanaContinueCard target={target} />;
  }
  return <AuthPendingState label="Opening Grafana…" testId="grafana-return" />;
}

/**
 * The catalogue's own read, `cp:app:read`. The manifest gates the `app-catalog` entry on
 * `anyOf: ['cp:app:read', 'cp:app:create']`, so a holder of `cp:app:create` alone sees the entry
 * and gets no summary fetch.
 *
 * ⛔ Spelled here because no `src/api` module owns it: the five modules that declare `cp:app:read`
 * each name their own screen (`APP_CONFIG_READ_PERMISSION` and four siblings), and importing one
 * of those here would say this gate is about that screen. `W4-21` checks that the `app-catalog`
 * entry's `anyOf` contains this constant, so the copy cannot drift.
 *
 * ⛔ **EXPORTED SO THE PIN HAS A SUBJECT** (AP-03). While it was module-private, `W4-21` compared
 * the manifest entry against a literal the test spelled itself, so changing this constant left the
 * arm green — a pin two docblocks claimed and nothing carried. The arm now reads this symbol and
 * its own literal, so a drift on either side reds.
 */
export const CATALOG_READ_PERMISSION = 'cp:app:read';

export interface CpAdminAppViewProps {
  readonly auth: ReturnType<typeof useAuth>;
  readonly capabilities: AuthCapabilities | null;
  readonly grants: ReturnType<typeof visibleGrantsState>;
  /**
   * The one fleet-wide availability summary state (#2621, R-021, R-041).
   *
   * ⛔ Required, not optional-with-a-default. An optional prop defaulting to `loading` would make
   * every test in the package silently assert that one arm — the same rule
   * `AuthenticatedShellProps.resourceGrants` states for the same reason.
   */
  readonly availabilitySummary: AvailabilitySummaryState;
  /**
   * The pending access-request count and its re-read (#2496, D25).
   *
   * ⛔ Required, not optional-with-a-default, for the reason `availabilitySummary` above states: an
   * optional prop defaulting to `loading` would make every test in the package silently assert that
   * one arm.
   *
   * ⛔ **A PROP HERE AND A CONTEXT BELOW, AND THE SPLIT IS NOT AN INCONSISTENCY.** This component is
   * pure, so the value has to arrive from the composed root that fetched it; and `PeoplePane` — the
   * consumer that calls `refresh` — is built by `AppLoader` from a manifest `loader` that passes a
   * pane nothing, so a prop cannot reach it. The prop ends here, at the provider.
   */
  readonly accessRequestSummary: AccessRequestSummaryValue;
}

/**
 * Pure view layer — takes auth state, capabilities and grant state as props.
 *
 * Separated from the composed `CpAdminApp` below so the four-branch ordering is assertable
 * without winning a race against `useAuth`'s mount effect. WS-C §7 assertion 1 (spinner first,
 * and NO navigation to `/login`) is only writable against this component.
 */
export function CpAdminAppView({
  auth,
  capabilities,
  grants,
  availabilitySummary,
  accessRequestSummary,
}: CpAdminAppViewProps): React.JSX.Element {
  /**
   * ⚠️ The dependency is `logout`, NOT `auth`. `useAuth` returns a fresh object literal every
   * render, so `[auth]` would change identity every render and this `useCallback` would be
   * decoration — memoised in appearance, unstable in fact. The individual members ARE stable.
   * The old console records this as a real defect it had to fix. ⛔ Do not "simplify" the dep.
   * The code reads the member into a local first, so the hooks lint can see the dependency.
   */
  const { logout } = auth;
  const onSignOut = useCallback(() => {
    void logout();
  }, [logout]);

  /*
   * Whether this document rendered the signed-out state: settled, with no session. A session that
   * branch 4 holds after that came from a sign-in in this tab, so `GrafanaReturnGate` may load
   * Grafana at once. A session that a boot restored never passes through it, and the gate then asks
   * for a click (plan decision 5). The flag lives in memory, so a new document starts it false.
   */
  const [signedOutSeen, setSignedOutSeen] = useState(false);
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) setSignedOutSeen(true);
  }, [auth.isLoading, auth.isAuthenticated]);

  /**
   * The memo is for REFERENCE STABILITY only — `PermissionProvider` memoises on array identity, so
   * a fresh snapshot each render churns every `useHasPermission` consumer in the tree.
   */
  const permissionSnapshot: PermissionSnapshot = useMemo(
    () => buildPermissionSnapshot(auth.siteRoles, auth.sitePermissions, grants.grants),
    [auth.siteRoles, auth.sitePermissions, grants.grants],
  );

  /*
   * ⛔ `<BrowserRouter basename>` WRAPS ALL FOUR BRANCHES, not just branch 4 — branches 2 and 3
   * are routed too, and the callback route in particular cannot work outside a router.
   *
   * ⛔ And `ConsentGate` is INSIDE the router and OUTSIDE the ternary. Inside, because it reads
   * `useLocation()` and a hook with no Router ancestor throws; outside, because branch 0 must be
   * able to win against any of the four (subject to branch 3 — see the gate's own header).
   */
  return (
    <BrowserRouter basename={BASENAME}>
      <SecondFactorGate auth={auth}>
      <StepUpGate auth={auth}>
      <ConsentGate auth={auth}>
        {auth.isLoading ? (
          /*
           * ⭐ **THE FOURTH PRE-AUTH SURFACE, and it was inline here — in no corpus and behind no gate.**
           * This is the genuine first paint of every session: every returning operator sees it before
           * anything else, and it was the one screen in the product that no primitive reached and no
           * ratchet counted. Its markup was byte-identical to `OAuthCallbackPage`'s pending arm except
           * for the frame, which had silently lost its `p-8` — invisible behind a centred spinner, and
           * exactly how a shared literal ends up with two versions.
           */
          <AuthPendingState label="Loading…" testId="auth-loading" />
        ) : !auth.isAuthenticated ? (
          <Routes>
            <Route
              path="/login"
              element={
                <LoginPage
                  capabilities={capabilities}
                  onLogin={auth.login}
                  onPasskeyLogin={auth.loginWithPasskey}
                />
              }
            />
            <Route
              path="/auth/oauth/callback"
              element={
                <OAuthCallbackPage onExchangeCode={auth.loginWithOAuthCode} takeReturnTo={takeReturnTo} />
              }
            />
            {/*
              ⛔ #2496 — THE THREE REFUSAL PAGES, AND THEY MUST BE SIBLINGS OF `/login` RATHER THAN
              ROUTES ANYWHERE ELSE. `cp-auth-api` 302s a refused OAuth callback straight at these
              paths, so the browser arrives holding no session; registered here they render, and
              registered anywhere else they fall to the splat below and bounce to `/login`, which
              is the same blank answer the raw JSON body was.

              ⛔ AND ABOVE THE SPLAT. React Router takes the best match rather than the first, so
              order is not what decides this today — but the splat is the only other thing in this
              block that can match these paths, and putting an explicit route after a catch-all is
              how the next reader learns the wrong rule.

              ⚠️ The paths come from `REFUSAL_PATHS`, never from a literal here. The literals live
              in one file in this package and are checked against the server's own table by
              `shell/__tests__/refusal-pages.test.tsx`. ⚠️ Indexing that array pairs a position
              with a page, so reordering it would swap the three screens silently — that same test
              renders each path and asserts the page it gets, which is what catches it.

              ⛔ `/access-requested` is THIRD because the server appends it third. The two lists are
              compared element by element, so the index here and the row there are one contract.
            */}
            <Route path={REFUSAL_PATHS[0]} element={<SignInUnavailablePage />} />
            <Route path={REFUSAL_PATHS[1]} element={<SignInIncompletePage />} />
            <Route path={REFUSAL_PATHS[2]} element={<AccessRequestedPage />} />
            {/*
              ⛔ STASH BEFORE BOUNCING. An unauthenticated visitor who followed a deep link must land
              back on it after signing in, not on the generic landing — `StashAndRedirectToLogin`
              records `window.location.pathname + search` (browser-absolute) and `takeReturnTo()`
              converts it once on the way out. WS-C §4.
            */}
            <Route path="*" element={<StashAndRedirectToLogin />} />
          </Routes>
        ) : auth.mustChangePassword ? (
          /* Branch 3 — EVERY path renders the rotation form, including `/apps/…`. No other route
             resolves, which is what makes the rotation compelled rather than suggested. The page
             records the path first, so the operator lands on it after the rotation (P-03). */
          <Routes>
            <Route path="*" element={<StashAndChangePassword onChangePassword={auth.changePassword} />} />
          </Routes>
        ) : (
          <GrafanaReturnGate signedInHere={signedOutSeen}>
          <MotionProvider>
            <PermissionProvider value={permissionSnapshot}>
              <GrantsStateProvider value={grants}>
                {/*
                  ⛔ BESIDE `GrantsStateProvider`, and inside `PermissionProvider` (#2621, R-021).
                  Inside, because the value was derived from the same two site-wide keys the
                  snapshot carries and a provider must not sit above the thing it agrees with.
                  Beside, because it is the same kind of thing: a value fetched once by this root
                  and carried to a pane that cannot be handed a prop.
                */}
                <AvailabilitySummaryProvider value={availabilitySummary}>
                {/*
                  ⛔ BESIDE `AvailabilitySummaryProvider`, for the same two reasons it sits where it
                  does (#2496, D25). INSIDE `PermissionProvider`, because the value was derived from
                  `cp:operator:read` — a site-wide key the snapshot also carries — and a provider
                  must not sit above the thing it agrees with. ABOVE `AuthenticatedShell`, because
                  both consumers are under it: the rail that `AppHostFrame` builds, and `PeoplePane`,
                  which `AppLoader` can hand no props.
                */}
                <AccessRequestSummaryProvider value={accessRequestSummary}>
                <CpBrandingProvider>
                  <AppsProvider
                    isAuthenticated={auth.isAuthenticated}
                    operatorId={auth.operatorId}
                  >
                    {/* The app list depends on the active role, so a switch reads it again. */}
                    <AppsFollowSiteRoles siteRoles={auth.siteRoles} />
                    {/*
                      B26 / G5 — the operator's own profile (the avatar in `UserMenu`).
                      ⛔ INSIDE this branch, so it is fetched only for an authenticated, non-rotation
                      session: branch 2 redirects to login and branch 3 compels a password rotation, and
                      a `GET /v1/auth/me` from either would be a request made by a session that has not
                      finished becoming one. ⚠️ It takes the SAME `isAuthenticated`/`operatorId` pair as
                      `AppsProvider` because it has the same staleness hazard — see the tag discussion in
                      `profile-context.tsx`.
                    */}
                    <ProfileProvider
                      isAuthenticated={auth.isAuthenticated}
                      operatorId={auth.operatorId}
                    >
                      {/* The data plane list of the rail picker. It reads nothing until a data
                          plane page asks, and it reads cp:dp:read from the snapshot above. */}
                      <DataPlaneChoicesProvider operatorId={auth.operatorId}>
                      <AppRegistryProvider>
                        {/* The step-up dialog of the kernel-rights panes (WS-9b), inside
                            `PermissionProvider` with the rest of the authenticated stack. */}
                        <StepUpProvider>
                          {/* The active site role of the session (plan 2026-10-22-cp-active-role):
                              the account menu, the elevation notice, the 403 panel, the
                              zero-access panel and the shell frame read it. Inside
                              `StepUpProvider`, because an elevation runs the step-up dialog. */}
                          <ActiveRoleProvider
                            operatorId={auth.operatorId}
                            heldSiteRoles={auth.heldSiteRoles}
                            siteRoles={auth.siteRoles}
                            restSiteRole={auth.restSiteRole}
                            elevatedUntilMs={auth.elevatedUntilMs}
                            switchActiveRole={auth.switchActiveRole}
                            refreshSession={auth.refreshSession}
                          >
                            <AuthenticatedShell
                              email={auth.email}
                              resourceGrants={grants.grants}
                              grantsError={grants.error}
                              onSignOut={onSignOut}
                              availabilitySummary={availabilitySummary}
                            />
                          </ActiveRoleProvider>
                        </StepUpProvider>
                      </AppRegistryProvider>
                      </DataPlaneChoicesProvider>
                    </ProfileProvider>
                  </AppsProvider>
                </CpBrandingProvider>
                </AccessRequestSummaryProvider>
                </AvailabilitySummaryProvider>
              </GrantsStateProvider>
            </PermissionProvider>
          </MotionProvider>
          </GrafanaReturnGate>
        )}
      </ConsentGate>
      </StepUpGate>
      </SecondFactorGate>
    </BrowserRouter>
  );
}

/**
 * Reads the app list again when the site roles of the session change: a role switch, or the end of
 * an elevation, changes which apps the session may list (`cp:app:list-all`). Renders nothing. Never
 * on the first render: the provider reads the list on mount.
 */
function AppsFollowSiteRoles({ siteRoles }: { readonly siteRoles: readonly string[] }): null {
  const { reload } = useAppsContext();
  const key = siteRoles.join(' ');
  const seen = useRef(key);
  useEffect(() => {
    if (seen.current === key) return;
    seen.current = key;
    reload();
  }, [key, reload]);
  return null;
}

export function CpAdminApp(): React.JSX.Element {
  const auth = useAuth();
  const [capabilities, setCapabilities] = useState<AuthCapabilities | null>(null);

  useEffect(() => {
    getCapabilities()
      .then(setCapabilities)
      // ⛔ Password-only on failure, never "render nothing" — a transient blip must not lock
      // everyone out. `LoginPage` owns the same fallback for the not-yet-settled case.
      .catch(() => setCapabilities(CAPABILITIES_FALLBACK));
  }, []);

  /*
   * The caller's own resource grants, as FETCHED and TAGGED with the identity they belong to.
   *
   * ⚠️ There is deliberately NO effect that resets this to "unknown". React flushes passive
   * effects AFTER the commit, so a reset-in-an-effect always leaves one commit holding the
   * PREVIOUS value — the unauthenticated branch's `grants: []` on sign-in (a denial shown before
   * anyone asked) and the PREVIOUS OPERATOR'S grants on an identity change (an over-permit). What
   * may be SHOWN is therefore derived during render by `visibleGrantsState`, which cannot show an
   * untagged or mis-tagged value at all.
   */
  const [fetched, setFetched] = useState<FetchedGrants | null>(null);
  const grants = visibleGrantsState(auth.isAuthenticated, auth.operatorId, fetched);
  /*
   * Bumped by `reloadGrants`, so the effect below reads the grants again for the same identity.
   * The previous answer stays in `fetched` until the new one lands: a reset would show one frame
   * in which the caller holds nothing.
   */
  const [grantsNonce, setGrantsNonce] = useState(0);
  /* The nonce of the last read that landed. A higher `grantsNonce` is a re-read in flight. */
  const [settledGrantsNonce, setSettledGrantsNonce] = useState(0);
  const grantsRereadPending = grantsNonce !== settledGrantsNonce;
  /* The failure of the last re-read beside the kept grants, hidden while the next re-read runs. */
  const grantsRereadError = grantsRereadPending
    ? null
    : visibleGrantsRereadError(auth.isAuthenticated, auth.operatorId, fetched);
  const reloadGrants = useCallback(() => {
    setGrantsNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    // #4436 (plan 2026-10-23-cp-must-change-password-gate, D10): the server answers 403
    // `password_change_required` to this read while the token carries `mustChangePassword`. So the
    // read waits for the change. The token the change adopts clears the flag, and this effect runs
    // then with that token.
    if (!auth.isAuthenticated || auth.mustChangePassword) return undefined;
    let live = true;
    // Captured, not read in the callback: the tag must be the identity the request was issued
    // FOR, not whoever is signed in when it lands.
    const forOperatorId = auth.operatorId;
    const nonce = grantsNonce;
    const settle = (state: GrantsState): void => {
      if (!live) return;
      // A failed fetch keeps the last usable list for this identity; a truncated answer replaces it.
      setFetched((previous) => settleFetchedGrants(previous, forOperatorId, state));
      setSettledGrantsNonce(nonce);
    };
    getMyGrants()
      .then((res) => settle(grantsStateFromResponse(res)))
      .catch((e: unknown) => settle(grantsStateFromError(e)));
    return () => {
      live = false;
    };
  }, [auth.isAuthenticated, auth.mustChangePassword, auth.operatorId, grantsNonce]);

  /*
   * ⛔ **THE TWO SITE-WIDE PRE-CHECKS FOR THE AVAILABILITY SUMMARY, COMPUTED HERE AND NOWHERE
   * ELSE** (#2621, R-038, R-041).
   *
   * ⛔ `hasPermission([...siteRoles, ...sitePermissions], k)` is the argument list
   * `useHasPermission(k)` resolves with no `resourceId`, so this root asks the same question the
   * pane asks. It cannot call the hook: this component RENDERS `PermissionProvider`, and a
   * component cannot read a context it provides. `DataPlaneActivityTab` uses the same spelling,
   * and `permission-gate-parity.test.ts` recognises it through `GATE_CALLS`.
   *
   * ⛔ **BOTH KEYS.** Since 2026-10-27 both name `cp:app:read`: the summary route moved off
   * `cp:billing:read` to the key of the per-app licence read. The two checks stay apart, so a
   * future gate move on either route changes one constant. Without the summary check, every
   * operator who lacks the key spends one refused round trip per console load. Without the
   * catalogue check, a principal spends a request neither surface will render (R-038).
   *
   * ⚠️ **THESE SAVE REQUESTS; THEY AUTHORIZE NOTHING.** The server's
   * `@RequirePermission('cp:app:read')` is the gate. A principal whose snapshot says yes and
   * whose server says no renders the same absence, because both routes reach `forbidden` (R-030).
   */
  const resolved = [...auth.siteRoles, ...auth.sitePermissions];
  const canReadLicences = hasPermission(resolved, AVAILABILITY_SUMMARY_PERMISSION);
  const canReadApps = hasPermission(resolved, CATALOG_READ_PERMISSION);

  /*
   * The one fleet-wide read, held exactly as `getMyGrants`'s result is held above.
   *
   * ⚠️ NO identity tag, unlike `FetchedGrants`. This count is a property of the FLEET, not of the
   * caller: the same two integers answer for every principal who may read them, so a value that
   * arrived for the previous operator is not an over-permit and needs no `forOperatorId`. The two
   * pre-checks above are what decide whether it may be SHOWN.
   */
  const [fetchedSummary, setFetchedSummary] = useState<AvailabilitySummaryState | null>(null);

  useEffect(() => {
    // ⛔ BOTH gates, and the early return is the request saved. See the pre-check block above.
    // #4436, D10: a token with `mustChangePassword` gets 403 here, so the read waits for the change.
    if (auth.mustChangePassword || !canReadLicences || !canReadApps) return undefined;
    let live = true;
    /*
     * ⛔ `allSettled`, so a rejection is DATA handed to `toSummaryState` rather than an unhandled
     * rejection — the same shape `fetchAvailability` uses in `availability-model.ts`, and the
     * reason `toSummaryState` takes a `PromiseSettledResult` at all. ⚠️ One element in, one
     * element out: `allSettled` resolves an array of its input's length, so `[result]` is never
     * `undefined` and a guard here would be an uncoverable branch.
     */
    void Promise.allSettled([fetchAvailabilitySummary()]).then(([result]) => {
      if (live) setFetchedSummary(toSummaryState(result));
    });
    return () => {
      live = false;
    };
    /*
     * ⛔ The deps are the two gates and the pending password change (#4436, D10), and nothing
     * else. The count is fleet-wide, so an identity dependency would re-issue one request per
     * operator switch for an answer that cannot have changed with the operator.
     */
  }, [auth.mustChangePassword, canReadLicences, canReadApps]);

  /*
   * ⛔ DERIVED DURING RENDER, never reset from an effect — the rule `visibleGrantsState` above
   * states with its measurement: React flushes passive effects after the commit, so a reset
   * written in an effect always leaves one commit holding the previous value. Here that matters
   * for the same reason: a principal who loses the summary key must read `forbidden` on the
   * render that loses it, not one commit later with the previous number still on screen.
   */
  const availabilitySummary: AvailabilitySummaryState = visibleSummaryState(
    canReadLicences,
    canReadApps,
    fetchedSummary,
  );

  /*
   * ⛔ **THE PENDING ACCESS-REQUEST COUNT (#2496, D25).** The pre-check is the SAME key the People
   * manifest entry carries, spelled as `hasPermission([...siteRoles, ...sitePermissions], k)` —
   * the argument list `useHasPermission(k)` resolves with no `resourceId`, so this root asks the
   * question the pane asks. It cannot call the hook: this component RENDERS `PermissionProvider`,
   * and a component cannot read a context it provides.
   *
   * ⛔ **THE EARLY RETURN IS THE REQUEST SAVED, NOT THE GATE.** Every principal who cannot open
   * People would otherwise spend one refused round trip per console load, and the server's
   * `@RequirePermission('cp:operator:read')` is what actually decides. A snapshot that says yes and
   * a server that says no reach the same `error` with status 403 (R-030).
   */
  const canReadOperators = hasPermission(resolved, ACCESS_REQUEST_READ_PERMISSION);

  const [fetchedRequests, setFetchedRequests] = useState<AsyncState<AccessRequestSummary> | null>(
    null,
  );
  /**
   * Bumped by `refresh` so the effect re-runs without the gate changing.
   *
   * ⚠️ The EFFECT issues the request, never `refresh` itself — `useApiResource` records the reason:
   * a `refresh` that fetched directly would race the mount effect and double-fire. ⛔ And there is
   * no timer here: the count is re-read when the People section changes it and on a fresh shell
   * mount, never on a poll.
   */
  const [requestsNonce, setRequestsNonce] = useState(0);
  const refreshAccessRequests = useCallback(() => {
    setRequestsNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    // #4436, D10: a token with `mustChangePassword` gets 403 here, so the read waits for the change.
    if (auth.mustChangePassword || !canReadOperators) return undefined;
    let live = true;
    getAccessRequestSummary().then(
      (data) => {
        if (live) setFetchedRequests({ kind: 'ready', data });
      },
      (cause: unknown) => {
        if (!live) return;
        /*
         * ⛔ The STATUS is carried, not just the sentence. It is what separates *"not yours"* from
         * *"we broke"* at every consumer, and `AsyncState`'s own docblock refuses to invent one:
         * `null` is a failure that never reached a response.
         */
        setFetchedRequests({
          kind: 'error',
          status: cause instanceof ApiError ? cause.status : null,
          message: apiErrorMessage(cause, 'The access-request count could not be read.'),
        });
      },
    );
    return () => {
      live = false;
    };
  }, [auth.mustChangePassword, canReadOperators, requestsNonce]);

  /*
   * ⛔ DERIVED DURING RENDER, never reset from an effect — the rule `visibleGrantsState` states
   * with its measurement. An operator who loses `cp:operator:read` must read the refusal on the
   * render that loses it, not one commit later with the previous number still in the chrome.
   *
   * ⚠️ `useMemo` for REFERENCE STABILITY only: the value is a fresh object literal each render
   * otherwise, and every consumer of the context would churn on every parent render.
   */
  const accessRequestSummary: AccessRequestSummaryValue = useMemo(
    () => ({
      state: visibleAccessRequestSummary(canReadOperators, fetchedRequests),
      refresh: refreshAccessRequests,
    }),
    [canReadOperators, fetchedRequests, refreshAccessRequests],
  );

  return (
    <GrantsReloadProvider value={reloadGrants} pending={grantsRereadPending} error={grantsRereadError}>
      <CpAdminAppView
        auth={auth}
        capabilities={capabilities}
        grants={grants}
        availabilitySummary={availabilitySummary}
        accessRequestSummary={accessRequestSummary}
      />
    </GrantsReloadProvider>
  );
}
